import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@telluscompany/design-system";
import { Save } from "lucide-react";

import { useLocalization } from "@i18n";
import { LoadingBlurDiv } from "@shared/components";
import { useUserContext } from "@shared/contexts";

import {
  ComplexCombobox,
  StructureCombobox,
  UserCombobox,
} from "../../../shared";
import {
  useAdminPermissionsQuery,
  usePutUserPermissionsMutation,
  type AdminPermissionsModule,
} from "../api/permissions";
import { AdminPermissionsGrid } from "../components/organisms";

/** Permissão base do catálogo: toda outra depende dela na UI Admin. */
const BASE_PERMISSION_KEY = "view";

// Só semeia o estado com módulos editáveis (`isAvailableInStructure=true`).
// O backend devolve `false` quando o módulo não está publicado (`isReleased`) ou,
// para alvo client, quando não há licença vigente (PUT: `3.20.72` / `3.20.35`).
// Tellus dispensa licença, mas não dispensa publicação.
const toSelectionMap = (
  modules: AdminPermissionsModule[],
): Record<string, Set<string>> => {
  const map: Record<string, Set<string>> = {};
  for (const mod of modules) {
    if (!mod.isAvailableInStructure) continue;
    map[mod.moduleId] = new Set(mod.grantedPermissionKeys);
  }
  return map;
};

const AdminPermissionsPage = () => {
  const { translate } = useLocalization();
  const { user: me } = useUserContext();

  const [complexId, setComplexId] = useState<string>("");
  const [structureId, setStructureId] = useState<string>("");
  const [userId, setUserId] = useState<string>("");

  const isReady = Boolean(complexId && structureId && userId);

  const { data, isFetching, isError } = useAdminPermissionsQuery(
    { complexId, structureId, userId },
    { enabled: isReady },
  );

  // Estado local dos checkboxes. Sincronizado com a resposta do GET cada vez
  // que o tenant carrega novas permissões; o submit envia esse mapa agregado
  // como um único PUT (bulk replace por estrutura).
  const [selected, setSelected] = useState<Record<string, Set<string>>>({});

  useEffect(() => {
    if (data?.modules) {
      setSelected(toSelectionMap(data.modules));
    }
  }, [data]);

  // Trocar complexo invalida a estrutura selecionada (a próxima precisa
  // pertencer ao novo complexo). Mantemos o userId — o backend valida escopo.
  const handleComplexChange = useCallback((value: string) => {
    setComplexId(value);
    setStructureId("");
    setSelected({});
  }, []);

  const handleStructureChange = useCallback((value: string) => {
    setStructureId(value);
    setSelected({});
  }, []);

  const handleUserChange = useCallback((value: string) => {
    setUserId(value);
    setSelected({});
  }, []);

  const handleToggle = useCallback(
    (moduleId: string, permissionKey: string) => {
      setSelected((prev) => {
        const next = { ...prev };
        const moduleSet = new Set(next[moduleId] ?? []);

        if (moduleSet.has(permissionKey)) {
          moduleSet.delete(permissionKey);
          // `view` é a permissão base: sem leitura, nenhuma outra permissão do
          // módulo faz sentido na UI (inspection-quality-test §2.2
          // QT-INSP-02-14). O backend não normaliza — o invariante é da UI.
          if (permissionKey === BASE_PERMISSION_KEY) moduleSet.clear();
        } else {
          moduleSet.add(permissionKey);
          // Conceder qualquer permissão implica `view` explícito
          // (QT-INSP-02-11 / QT-INSP-02-13).
          moduleSet.add(BASE_PERMISSION_KEY);
        }

        next[moduleId] = moduleSet;
        return next;
      });
    },
    [],
  );

  const mutation = usePutUserPermissionsMutation();

  // ownerViewer só pode ler (mesma regra de demais telas administrativas);
  // owner sempre escreve; complexAdmin chega aqui pelo adminLoader e o backend
  // valida o escopo dele no PUT.
  const readOnly = me?.role === "ownerViewer";
  const canSave = isReady && !readOnly && !isFetching && !!data;

  const payloadModules = useMemo(() => {
    return Object.entries(selected).map(([moduleId, keys]) => ({
      moduleId,
      permissionKeys: Array.from(keys),
    }));
  }, [selected]);

  const handleSave = () => {
    if (!isReady) return;
    mutation.mutate({
      userId,
      complexId,
      structureId,
      modules: payloadModules,
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold">
          {translate("admin.permissions.page.title")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {translate("admin.permissions.page.subtitle")}
        </p>
      </header>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <ComplexCombobox
          label={translate("admin.permissions.form.label.complex")}
          placeholder={translate(
            "admin.permissions.form.placeholder.selectComplex",
          )}
          clearable
          value={complexId}
          onChange={handleComplexChange}
        />
        <StructureCombobox
          complexId={complexId || null}
          label={translate("admin.permissions.form.label.structure")}
          placeholder={translate(
            "admin.permissions.form.placeholder.selectStructure",
          )}
          clearable
          value={structureId}
          onChange={handleStructureChange}
        />
        <UserCombobox
          onlyRoleUser
          label={translate("admin.permissions.form.label.user")}
          placeholder={translate(
            "admin.permissions.form.placeholder.selectUser",
          )}
          clearable
          value={userId}
          onChange={handleUserChange}
        />
      </div>

      {!isReady ? (
        <p className="text-sm text-muted-foreground">
          {translate("admin.permissions.empty.selectAll")}
        </p>
      ) : (
        <LoadingBlurDiv isLoading={isFetching}>
          <div className="flex flex-col gap-4">
            {isError ? (
              <p className="text-sm text-muted-foreground">
                {translate("admin.permissions.error.load")}
              </p>
            ) : (
              <AdminPermissionsGrid
                modules={data?.modules ?? []}
                selected={selected}
                onToggle={handleToggle}
                readOnly={readOnly}
              />
            )}

            {!readOnly && (
              <div className="flex justify-end">
                <Button
                  variant="primary"
                  onClick={handleSave}
                  disabled={!canSave || mutation.isPending}
                  iconLeft={<Save />}
                >
                  {translate("admin.permissions.button.save")}
                </Button>
              </div>
            )}
          </div>
        </LoadingBlurDiv>
      )}
    </div>
  );
};

export default AdminPermissionsPage;
